/**
 * Every store in the tab, and the routing table from channel to store.
 *
 * The flush drains one channel's buffer and hands the frames here; this file
 * knows which store each channel feeds and nothing else knows. Two things are
 * worth reading closely.
 *
 * **The checksum is verified here, not in the sequencer.** A book delta's
 * checksum is a statement about the ladder *after* the delta is applied, and the
 * ladder does not exist until this file mutates it. `seq` is checked on receipt
 * because it decides whether a frame may be buffered at all; the checksum is
 * checked at flush because that is the first moment there is anything to check.
 * `apply()` returns the verdicts and the caller feeds them to the sequencer,
 * which owns the recovery decision.
 *
 * **A fill can arrive before the position frame it caused.** There is no
 * cross-channel ordering guarantee (docs/03 §4.4), so every fill's own
 * `post_position` is recorded as a *hint* alongside the positions store rather
 * than merged into it. The server's object stays the server's object — nothing
 * here invents a position — and the hint is dropped the moment a real frame
 * arrives that is at least as new.
 */

import type { ChannelFrame, ChannelName } from '@/api/ws/protocol'
import { parseChannel, type ParsedChannel } from '@/api/ws/channels'
import { positionHintFromFill, type PositionHint } from '@/api/ws/dedupe'
import { BookStore, type BookDeltaPayload, type BookSnapshotPayload } from './book'
import { KeyedStore, LogStore, SingletonStore, DEFAULT_LOG_CAPACITY } from './entities'
import type { PendingOrder } from './pending-orders'
import { RESTING_STATUSES } from '@/domain/pro-ladder'

/** The one method the flush needs from a store. */
export interface Publishable {
  notify(): boolean
}

export interface ChecksumVerdict {
  channel: ChannelName
  expected: number
  computed: number
  ok: boolean
}

export interface ApplyResult {
  applied: number
  checksums: ChecksumVerdict[]
}

/** Rows kept per tape. 500 covers the whole life of a market at the 25 f/s peak
 *  and then some; the archive is `GET /markets/{id}/trades`. */
export const TAPE_CAPACITY = 500

/** Messages kept per chat room (§F137). Four snapshots' worth: a room is read,
 *  not archived, and `GET /chat/{asset}/messages` pages further back. */
export const CHAT_CAPACITY = 200

export class StoreSet {
  /** `config.ws.checksum_levels`. Passed in, never assumed (plan §2.12). */
  readonly #checksumLevels: number

  // Public, per market
  readonly books = new Map<string, BookStore>()
  readonly trades = new Map<string, LogStore>()
  readonly tickers = new Map<string, SingletonStore>()
  readonly candles = new Map<string, KeyedStore>()
  readonly markets = new Map<string, SingletonStore>()
  readonly series = new Map<string, SingletonStore>()
  readonly indexes = new Map<string, SingletonStore>()
  /** Public, per asset: the troll box (§F137). Keyed by `message_id`, because
   *  a message posted as a tab subscribes can arrive in both the snapshot and
   *  the first batch, and must render once. */
  readonly chats = new Map<string, LogStore>()
  readonly heartbeat = new SingletonStore()

  // Private, one each
  readonly orders = new KeyedStore((v: unknown) => idOf(v, 'order_id'))
  /** `keyOf` is what makes a fill revisable in place: under D-B1 the same
   *  `fill_id` arrives twice, the second time with `settlement_tx_hash`
   *  populated (§F31). `settlements` and `notifications` take no key — their
   *  entries are facts that never change. */
  readonly fills = new LogStore(DEFAULT_LOG_CAPACITY, (v: unknown) => idOf(v, 'fill_id'))
  readonly positions = new KeyedStore((v: unknown) => idOf(v, 'market_id'))
  readonly balance = new SingletonStore()
  readonly settlements = new LogStore()
  readonly notifications = new LogStore()

  /**
   * Positions implied by a fill whose `positions` frame has not arrived.
   *
   * Kept apart from `positions` on purpose: a hint is three fields off a fill,
   * not a position object, and merging them would put a row on screen that looks
   * like the server's and is missing `cost_basis`, `settlement_status` and every
   * other field the panel renders.
   */
  readonly positionHints = new KeyedStore((v: PositionHint) => v.marketId)

  /**
   * Orders this tab has submitted and not yet seen back, keyed by
   * `client_order_id`.
   *
   * Retired by `retirePending`, which both arrival paths call — see
   * `pending-orders.ts` for the race and why the two maps are worth it.
   */
  readonly pendingOrders = new KeyedStore((v: PendingOrder) => v.clientOrderId)

  constructor(options: { checksumLevels: number }) {
    this.#checksumLevels = options.checksumLevels
  }

  // -------------------------------------------------------------------------

  bookFor(marketId: string): BookStore {
    return getOrCreate(this.books, marketId, () => new BookStore())
  }

  tradesFor(marketId: string): LogStore {
    return getOrCreate(this.trades, marketId, () => new LogStore(TAPE_CAPACITY))
  }

  chatFor(asset: string): LogStore {
    return getOrCreate(this.chats, asset, () => new LogStore(CHAT_CAPACITY, (v: unknown) => idOf(v, 'message_id')))
  }

  tickerFor(marketId: string): SingletonStore {
    return getOrCreate(this.tickers, marketId, () => new SingletonStore())
  }

  /** Keyed by the full channel name: one market has a `1s` chart and a `5s`
   *  sparkline and they are different series of buckets. */
  candlesFor(channel: string): KeyedStore {
    return getOrCreate(this.candles, channel, () => new KeyedStore((v: unknown) => idOf(v, 'open_time_ms')))
  }

  marketFor(marketId: string): SingletonStore {
    return getOrCreate(this.markets, marketId, () => new SingletonStore())
  }

  seriesFor(seriesId: string): SingletonStore {
    return getOrCreate(this.series, seriesId, () => new SingletonStore())
  }

  indexFor(symbol: string): SingletonStore {
    return getOrCreate(this.indexes, symbol, () => new SingletonStore())
  }

  // -------------------------------------------------------------------------

  /**
   * Apply one channel's drained frames, in arrival order.
   *
   * Frames only — never raw payloads — because `type` decides the operation:
   * `snapshot` replaces, `delta` mutates, `update` and `event` are per-channel.
   */
  apply(channel: ChannelName, frames: readonly ChannelFrame[]): ApplyResult {
    const descriptor = parseChannel(channel)
    const checksums: ChecksumVerdict[] = []
    let applied = 0

    for (const frame of frames) {
      if (this.#applyFrame(descriptor, frame, checksums)) applied += 1
    }
    return { applied, checksums }
  }

  #applyFrame(
    descriptor: ParsedChannel,
    frame: ChannelFrame,
    checksums: ChecksumVerdict[],
  ): boolean {
    const data = frame.data
    if (data === undefined || data === null) return false
    const channel = descriptor.name as ChannelName

    switch (descriptor.kind) {
      case 'book': {
        const book = this.bookFor(descriptor.marketId as string)
        if (frame.type === 'snapshot') book.applySnapshot(data as BookSnapshotPayload)
        else book.applyDelta(data as BookDeltaPayload)

        const expected = (data as BookSnapshotPayload).checksum
        if (typeof expected === 'number') {
          const computed = book.checksum(this.#checksumLevels)
          checksums.push({ channel, expected, computed, ok: expected === computed })
        }
        return true
      }
      case 'trades': {
        const tape = this.tradesFor(descriptor.marketId as string)
        // A `snapshot` on this channel is the whole recent tape; an `event` is
        // one print. Both arrive newest-last on the wire and newest-first here.
        if (frame.type === 'snapshot') tape.seed(reverse(arrayOf(data, 'trades')))
        else tape.append(data)
        return true
      }
      case 'ticker':
        this.tickerFor(descriptor.marketId as string).set(data)
        return true
      case 'chat': {
        // Both the snapshot and every event carry `messages` oldest first; the
        // store keeps newest first, like the tape.
        const room = this.chatFor(descriptor.asset as string)
        const messages = arrayOf(data, 'messages')
        if (frame.type === 'snapshot') room.seed(reverse(messages))
        else room.appendAll(messages)
        return true
      }
      case 'candles': {
        const buckets = this.candlesFor(descriptor.name)
        if (frame.type === 'snapshot') buckets.seed(arrayOf(data, 'candles'))
        else buckets.upsert(data)
        return true
      }
      case 'market':
        this.marketFor(descriptor.marketId as string).set(data)
        return true
      case 'series':
        this.#applySeries(descriptor.seriesId as string, frame.type, data)
        return true
      case 'index':
        this.indexFor(descriptor.symbol as string).set(data)
        return true
      case 'heartbeat':
        this.heartbeat.set(data)
        return true
      case 'orders': {
        if (frame.type === 'snapshot') {
          this.#applyOrdersSnapshot(arrayOf(data, 'orders'), frame.server_time_ms)
        } else {
          this.#applyOrder(data)
        }
        return true
      }
      case 'fills': {
        if (frame.type === 'snapshot') {
          this.fills.seed(reverse(arrayOf(data, 'fills')))
        } else {
          this.fills.append(data)
          this.#applyPositionHint(data)
        }
        return true
      }
      case 'positions': {
        if (frame.type === 'snapshot') {
          for (const position of arrayOf(data, 'positions')) this.#applyPosition(position)
        } else {
          this.#applyPosition(data)
        }
        return true
      }
      case 'balance':
        this.balance.set(data)
        return true
      case 'settlements': {
        if (frame.type === 'snapshot') this.settlements.seed(reverse(arrayOf(data, 'settlements')))
        else this.settlements.append(data)
        return true
      }
      case 'notifications': {
        if (frame.type === 'snapshot') this.notifications.seed(reverse(arrayOf(data, 'notifications')))
        else this.notifications.append(data)
        return true
      }
    }
  }

  /**
   * `series.{id}` carries **two different objects**, and a `set()` for both
   * loses the rail.
   *
   * The `snapshot` is `SeriesSnapshotData` — the whole rail, `markets[]`
   * included. Every subsequent frame is an `event`: a `SeriesEventData` naming
   * one lifecycle transition, with no `markets[]` at all. Replacing the store's
   * value with an event therefore deletes the rail on the very first transition
   * after subscribe — which at a 60-second cadence is within a second or two of
   * every cold load, and is what `<SeriesRail>` found the first time anything
   * read this channel (register §F18i).
   *
   * So: a snapshot replaces, an event *merges*. The event's `state` /
   * `state_seq` are applied to the matching rail entry, `market` replaces that
   * entry outright on the four events that carry the full object, and
   * `next_market_id` — present on every event, by contract — updates the
   * pointer. The `markets[]` array is rebuilt rather than mutated because the
   * rail renders from it and plan §2.3's mutate-in-place rule is for the
   * ladder, not for a five-element list that changes sixty times an hour.
   */
  #applySeries(seriesId: string, type: ChannelFrame['type'], data: unknown): void {
    const store = this.seriesFor(seriesId)
    if (type === 'snapshot') {
      store.set(withRailHistory(store.value, data))
      return
    }

    const held = store.value as Record<string, unknown> | null
    // No snapshot yet — an event on its own is not a rail, and inventing one
    // from a single transition would put a one-chip rail on screen.
    if (!held) return

    const event = data as Record<string, unknown>
    const marketId = typeof event.market_id === 'string' ? event.market_id : null
    const rows = Array.isArray(held.markets) ? (held.markets as Record<string, unknown>[]) : []

    const next: Record<string, unknown> = { ...held }
    if (marketId) {
      const full = event.market as Record<string, unknown> | undefined
      // The full object first, then the flat fields over the top. `outcome` is
      // the reason the order matters: it lives at `resolution.outcome` on a
      // `Market` and flat on the event, so taking the object alone would drop
      // the one field a resolved rail chip renders.
      const patch = {
        market_id: marketId,
        ...full,
        ...(event.state === undefined ? {} : { state: event.state }),
        ...(event.state_seq === undefined ? {} : { state_seq: event.state_seq }),
        ...(event.anchor_ms === undefined ? {} : { anchor_ms: event.anchor_ms }),
        ...(event.expiry_at_ms === undefined ? {} : { expiry_at_ms: event.expiry_at_ms }),
        ...(event.outcome === undefined ? {} : { outcome: event.outcome }),
      }
      const at = rows.findIndex((row) => row.market_id === marketId)
      next.markets =
        at >= 0
          ? rows.map((row, i) => (i === at ? { ...row, ...patch } : row))
          : // A market the rail has not seen: append and let the renderer sort.
            // It arrives ahead of time by design — `market_created` fires
            // `n_ahead` periods out.
            [...rows, patch]
    }
    if (typeof event.next_market_id === 'string') next.next_market_id = event.next_market_id
    if (event.prefetch !== undefined) next.prefetch = event.prefetch
    store.set(next)
  }

  /**
   * One order in, from either transport, under the one merge rule.
   *
   * **Apply-if-newer, then retire the optimistic row.** `version` is monotonic
   * per order and is the apply-if-newer key on the `orders` channel (§7.1) — and
   * the POST receipt goes through this same method, so a receipt that arrives
   * *after* the frame it describes cannot roll the row back to a state the user
   * has already moved past. That is the whole reason this is a method rather
   * than a bare `upsert`: the socket path and the HTTP path used to be two
   * writes with two different opinions about staleness.
   */
  #applyOrder(order: unknown): boolean {
    const orderId = idOf(order, 'order_id')
    if (orderId === null) return false

    const held = this.orders.get(orderId)
    const heldVersion = held === undefined ? null : numberOf(held, 'version')
    const incoming = numberOf(order, 'version')
    // A missing `version` on either side means "cannot compare", and the newest
    // arrival wins — which is the pre-Phase-6 behaviour and correct for a
    // snapshot seeded from REST, where there is nothing to be newer than.
    const stale = heldVersion !== null && incoming !== null && incoming <= heldVersion
    if (!stale) this.orders.upsert(order)

    // Unconditional, and outside the staleness branch: a stale-by-version frame
    // still proves the order exists, which is the only thing the pending row was
    // standing in for.
    const clientOrderId = idOf(order, 'client_order_id')
    if (clientOrderId !== null) this.retirePending(clientOrderId)
    return !stale
  }

  /**
   * An `orders` snapshot is **every open order this account has**
   * (`ordersSnapshot`, and `GET /orders?status=OPEN` over REST), so it replaces
   * the open set rather than merging into it.
   *
   * It used to merge, which meant an order missing from the snapshot — because
   * it filled, or was cancelled, while this client was not listening — stayed
   * `OPEN` here for the life of the tab. A resubscribe is the one moment a
   * missed frame can be corrected, and merging threw that away (2026-10-08: the
   * pro ladder drew filled bids at prices the book no longer had).
   *
   * Two things are kept. A **terminal** order the store holds is history, and
   * the snapshot says nothing about history. And an order **touched after the
   * server built the snapshot** — a POST receipt that overtook it — is newer
   * than the frame and is not the frame's to remove. A snapshot stamped `0` is
   * the REST recovery path (`api/ws/client.ts`), which carries no time to
   * compare against, and removes on absence alone.
   */
  #applyOrdersSnapshot(orders: readonly unknown[], serverTimeMs: number): void {
    const present = new Set<string>()
    for (const order of orders) {
      const id = idOf(order, 'order_id')
      if (id !== null) present.add(id)
      this.#applyOrder(order)
    }
    const gone: string[] = []
    for (const [id, held] of this.orders.store.state.byId) {
      if (present.has(id)) continue
      if (!RESTING_STATUSES.has(String((held as Record<string, unknown>)?.status))) continue
      const updatedAt = numberOf(held, 'updated_at_ms')
      if (serverTimeMs > 0 && updatedAt !== null && updatedAt > serverTimeMs) continue
      gone.push(id)
    }
    for (const id of gone) this.orders.remove(id)
  }

  /**
   * The POST receipt's way in. Identical to a frame's, deliberately.
   *
   * `POST /orders` returns the whole order object with `status`, `filled_size`
   * and `version` already populated (§7.11), so it is an `orders` payload that
   * happened to arrive over HTTP. Routing it through the same merge means the
   * two transports cannot disagree about what the row says.
   */
  applyOrderReceipt(order: unknown): boolean {
    return this.#applyOrder(order)
  }

  /** Idempotent. Called by both arrival paths and by a rejection. */
  retirePending(clientOrderId: string): boolean {
    return this.pendingOrders.remove(clientOrderId)
  }

  #applyPosition(position: unknown): void {
    if (!this.positions.upsert(position)) return
    const marketId = idOf(position, 'market_id')
    if (marketId === null) return
    // The server's own object supersedes anything a fill implied about the same
    // market, whichever order the two arrived in.
    const hint = this.positionHints.get(marketId) as PositionHint | undefined
    if (!hint) return
    const updatedAt = numberOf(position, 'updated_at_ms')
    if (updatedAt === null || updatedAt >= hint.atMs) this.positionHints.remove(marketId)
  }

  #applyPositionHint(fill: unknown): void {
    const hint = positionHintFromFill(fill)
    if (!hint) return
    const held = this.positions.get(hint.marketId)
    const heldAt = held === undefined ? null : numberOf(held, 'updated_at_ms')
    // A positions frame that is already at least as new says everything the hint
    // would have; recording it anyway would put a provisional badge on a row the
    // server has already confirmed.
    if (heldAt !== null && heldAt >= hint.atMs) return
    const existing = this.positionHints.get(hint.marketId) as PositionHint | undefined
    if (existing && existing.atMs >= hint.atMs) return
    this.positionHints.upsert(hint)
  }

  // -------------------------------------------------------------------------

  /**
   * Every store, for the flush to notify in one pass.
   *
   * Typed as the one method the flush uses. `ExternalStore<T>` is invariant in
   * `T`, so a heterogeneous list of them would need a cast per entry to say
   * something the caller does not need to know.
   *
   * **A generator rather than an array, and that is the whole of the change.**
   * This ran once per animation frame and built a fresh array by spreading
   * seven maps into it — 89 entries and eight intermediate arrays for a tab
   * holding twenty markets, sixty times a second, to call a method that returns
   * `false` for almost all of them. `yield` walks the same stores and allocates
   * nothing (`docs/17`). It stays public because `allStores().length` is how
   * `notifyAll`'s own cost is measured.
   */
  *allStores(): Generator<Publishable> {
    for (const book of this.books.values()) yield book.store
    for (const trades of this.trades.values()) yield trades.store
    for (const ticker of this.tickers.values()) yield ticker.store
    for (const candles of this.candles.values()) yield candles.store
    for (const market of this.markets.values()) yield market.store
    for (const series of this.series.values()) yield series.store
    for (const index of this.indexes.values()) yield index.store
    for (const chat of this.chats.values()) yield chat.store
    yield this.heartbeat.store
    yield this.orders.store
    yield this.pendingOrders.store
    yield this.fills.store
    yield this.positions.store
    yield this.positionHints.store
    yield this.balance.store
    yield this.settlements.store
    yield this.notifications.store
  }

  /**
   * How many stores the last `notifyAll` had to walk.
   *
   * Exported for the same reason B8 exported an `eth_call` counter and the
   * repeated-work pass exported `BookRenders`: this cost is invisible in the
   * output. A tab that leaks a store per minute publishes byte-identical frames
   * to one that does not, so the assertion has to be a count.
   */
  #lastWalked = 0

  get storesWalked(): number {
    return this.#lastWalked
  }

  /** Publish every dirty store, once. Returns how many actually changed. */
  notifyAll(): number {
    let changed = 0
    let walked = 0
    for (const store of this.allStores()) {
      walked += 1
      if (store.notify()) changed += 1
    }
    this.#lastWalked = walked
    return changed
  }

  /**
   * Drop every per-market store the tab no longer subscribes.
   *
   * **Derived from the desired set, per family, rather than from a second list
   * of deletes** — which is invariant 18's own shape applied to the stores:
   * `desiredChannels()` already stages the roll exactly (depth until the
   * teardown, `market.{id}` until the retire), so following it reproduces that
   * staging and needs no separate schedule to keep in step with it.
   *
   * It closes two leaks `dropMarket` structurally could not, because
   * `RollScheduler` fires `TEARDOWN` and `RETIRE` for the **focused asset's**
   * predecessor only:
   *
   * - the desired set carries `market.{id}` for *both* assets (`desiredChannels`
   *   says why), so the unfocused asset's market store was created every minute
   *   and dropped never — 1,440 of them a day;
   * - `switchAsset` unsubscribes the departing asset's depth channels and left
   *   its book, tape, ticker and candle stores behind, one full set per switch.
   *
   * Per family and not per market id, because the retained ticker
   * (`switchAsset`) is a depth-family channel for a market whose depth is
   * otherwise gone — reading the channel names keeps that case with no special
   * case in it.
   *
   * **Never call this while the depth is suspended for a hidden tab.** The
   * subscriptions are gone then and the stores must not be: §9.2.4 rule 1 is
   * *re-render immediately from local state, marked STALE — never blank*, and a
   * tab that dropped its ladder at ninety seconds hidden would come back to an
   * empty book rather than a stale one. `Runtime#applyHolds` is the one caller
   * and it holds that condition.
   */
  retainSubscribed(channels: Iterable<string>): number {
    const keep = { book: new Set<string>(), trades: new Set<string>(), ticker: new Set<string>(), candles: new Set<string>(), market: new Set<string>() }
    for (const channel of channels) {
      const dot = channel.indexOf('.')
      if (dot < 0) continue
      const family = channel.slice(0, dot)
      if (!(family in keep)) continue
      // `candles.{marketId}.{timeframe}` is keyed by the whole channel name; the
      // other four are keyed by the market id, which is the rest of the string.
      keep[family as keyof typeof keep].add(family === 'candles' ? channel : channel.slice(dot + 1))
    }
    let dropped = 0
    const prune = (map: Map<string, unknown>, kept: Set<string>): void => {
      for (const key of [...map.keys()]) {
        if (kept.has(key)) continue
        map.delete(key)
        dropped += 1
      }
    }
    prune(this.books, keep.book)
    prune(this.trades, keep.trades)
    prune(this.tickers, keep.ticker)
    prune(this.candles, keep.candles)
    prune(this.markets, keep.market)
    return dropped
  }

  /**
   * Drop one market's **depth** — book, tape, ticker and candles. The `T+74`
   * teardown, and the batched `unsubscribe` of docs/03 §6.2's `T+74` row is its
   * other half.
   *
   * Separate from `dropMarket` because the two moments are sixteen seconds
   * apart and the brief conflated them: §6.2 tears the depth channels down at
   * `T+74` and keeps `market.{N}` **until `T+90`**, because the market is
   * `SETTLED`, the receipt links to it, and the receipt is still on screen at
   * `T+81`. One function doing both would either delete the market object out
   * from under a rendered receipt or leave four dead ladders subscribed for a
   * further sixteen seconds (register §F25).
   */
  dropMarketDepth(marketId: string): void {
    this.books.delete(marketId)
    this.trades.delete(marketId)
    this.tickers.delete(marketId)
    for (const key of [...this.candles.keys()]) {
      if (key.startsWith(`candles.${marketId}.`)) this.candles.delete(key)
    }
  }

  /** Drop everything for one market, `market.{id}` included — `T+90`. */
  dropMarket(marketId: string): void {
    this.dropMarketDepth(marketId)
    this.markets.delete(marketId)
  }
}

// ---------------------------------------------------------------------------

function getOrCreate<T>(map: Map<string, T>, key: string, create: () => T): T {
  const existing = map.get(key)
  if (existing !== undefined) return existing
  const created = create()
  map.set(key, created)
  return created
}

function idOf(value: unknown, field: string): string | null {
  const record = value as Record<string, unknown> | null
  if (typeof record !== 'object' || record === null) return null
  const raw = record[field]
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number') return String(raw)
  return null
}

function numberOf(value: unknown, field: string): number | null {
  const record = value as Record<string, unknown> | null
  if (typeof record !== 'object' || record === null) return null
  const raw = record[field]
  return typeof raw === 'number' ? raw : null
}

/**
 * A snapshot payload is an object carrying the list under a channel-specific
 * key; a bare array is accepted too, because that is what a REST body seeded
 * through the same path looks like.
 */
function arrayOf(data: unknown, field: string): unknown[] {
  if (Array.isArray(data)) return data
  const record = data as Record<string, unknown> | null
  if (typeof record !== 'object' || record === null) return []
  const named = record[field]
  if (Array.isArray(named)) return named
  const generic = record.data
  return Array.isArray(generic) ? generic : []
}

function reverse(values: readonly unknown[]): unknown[] {
  return [...values].reverse()
}

/**
 * A rail snapshot, over whatever rail the client already had.
 *
 * **A narrower window is not evidence a market stopped existing.** The snapshot
 * replaces every scalar on the series object — it is authoritative for
 * `current_market_id`, `next_market_id`, the prefetch block and the rest — but
 * its `markets[]` is a *moving window*, and the one the socket sends starts at
 * the resolved-previous while `GET /series/{id}/markets` seeds two behind it.
 * A plain `set()` therefore deleted the older half a moment after the cold load
 * had fetched it, and again on every reconnect, which is what left
 * `<SeriesRail>` with one chip behind the live market instead of two.
 *
 * So the two are unioned by `market_id`, the incoming row winning wherever both
 * have one, and the result is bounded: the rail renders a six-anchor window and
 * `RAIL_HISTORY` is several times that, so this keeps enough for a reconnect to
 * land on a full rail and not enough to grow with uptime.
 */
function withRailHistory(held: unknown, incoming: unknown): unknown {
  const heldRows = (held as { markets?: unknown } | null)?.markets
  const next = incoming as Record<string, unknown> | null
  if (!Array.isArray(heldRows) || !next || typeof next !== 'object') return incoming

  const byId = new Map<string, Record<string, unknown>>()
  for (const row of heldRows as Record<string, unknown>[]) {
    const id = idOf(row, 'market_id')
    if (id !== null) byId.set(id, row)
  }
  const incomingRows = Array.isArray(next.markets) ? (next.markets as Record<string, unknown>[]) : []
  for (const row of incomingRows) {
    const id = idOf(row, 'market_id')
    if (id !== null) byId.set(id, { ...byId.get(id), ...row })
  }

  const markets = [...byId.values()]
    .sort((a, b) => numberOr(a, 'anchor_ms') - numberOr(b, 'anchor_ms'))
    .slice(-RAIL_HISTORY)
  return { ...next, markets }
}

/** Rail entries kept across a snapshot. Bounded so a long session does not
 *  accumulate one row a minute for a rail that renders six. */
const RAIL_HISTORY = 24

function numberOr(row: Record<string, unknown>, field: string): number {
  const value = row[field]
  return typeof value === 'number' ? value : 0
}
